// CalendarioCitas.js
import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import "../../css/CalendarioCitas.css";

const estadoToColors = {
  programada: { backgroundColor: "#007bff", borderColor: "#007bff" },
  confirmada: { backgroundColor: "#28a745", borderColor: "#28a745" },
  cancelada: { backgroundColor: "#dc3545", borderColor: "#dc3545" },
};

const simulateDelay = (ms) => new Promise((r) => setTimeout(r, ms));

const DUMMY_CITAS = [
  {
    id: 101,
    placa: "ABC123",
    nombreCliente: "Juan Pérez",
    servicio: "Cambio de aceite",
    fecha: "2025-10-20T00:00:00",
    hora: "09:00:00",
    tipoVehiculo: "Mazda",
    modeloVehiculo: "3",
    estado: "programada",
    canal: "whatsapp",
    fechaRegistro: "2025-10-15T10:00:00",
    telefono: null,
  },
  {
    id: 102,
    placa: "XYZ789",
    nombreCliente: "María Gómez",
    servicio: "Mantenimiento general",
    fecha: "2025-10-21T00:00:00",
    hora: "14:00:00",
    tipoVehiculo: "Renault",
    modeloVehiculo: "Duster",
    estado: "confirmada",
    canal: "telefono",
    fechaRegistro: "2025-10-16T12:00:00",
    telefono: "3001234567",
  },
  {
    id: 103,
    placa: "QWE456",
    nombreCliente: "Pedro López",
    servicio: "Lavado completo",
    fecha: "2025-10-22T00:00:00",
    hora: "11:00:00",
    tipoVehiculo: "Toyota",
    modeloVehiculo: "Corolla",
    estado: "cancelada",
    canal: "whatsapp",
    fechaRegistro: "2025-10-16T15:00:00",
    telefono: null,
  },
];

const combinarFechaHoraISO = (fecha, hora = "00:00:00") => {
  if (!fecha) return null;
  const f = new Date(fecha);
  if (isNaN(f)) return null;
  return `${f.toISOString().split("T")[0]}T${hora}`;
};

const CalendarioCitas = () => {
  const [eventos, setEventos] = useState([]);
  const [rawCitas, setRawCitas] = useState([]);
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);

  // Filtros
  const [filtros, setFiltros] = useState({
    estado: "todos",
    canal: "todos",
    placa: "",
  });

  useEffect(() => {
    fetchCitas();
  }, []);
  useEffect(() => {
    if (citaSeleccionada && editMode) setForm({ ...citaSeleccionada });
  }, [citaSeleccionada, editMode]);

  const fetchCitas = async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        "https://localhost:7126/api/Citas/ObtenerCitasAgendadas",
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!resp.ok) {
        console.warn("Backend no disponible. Usando DUMMY...");
        await simulateDelay(300);
        usarDummy();
        return;
      }
      const data = await resp.json();
      procesarYSetCitas(data);
    } catch (err) {
      console.error(err);
      console.warn("Error fetch, usando DUMMY.");
      await simulateDelay(300);
      usarDummy();
    } finally {
      setLoading(false);
    }
  };

  const usarDummy = () => {
    setRawCitas(DUMMY_CITAS);
    setEventos(DUMMY_CITAS.map(mapCitaAEvento));
  };
  const procesarYSetCitas = (data) => {
    setRawCitas(data);
    setEventos(data.map(mapCitaAEvento));
  };

  const mapCitaAEvento = (cita) => {
    const startIso = combinarFechaHoraISO(cita.fecha, cita.hora);
    const estado = (cita.estado || "programada").toLowerCase();
    const colors = estadoToColors[estado] || estadoToColors["programada"];
    return {
      id: cita.id,
      title: `${cita.placa || ""} — ${cita.servicio || "Sin servicio"}`,
      start: startIso || (cita.fecha ? cita.fecha.split("T")[0] : null),
      extendedProps: cita,
      backgroundColor: colors.backgroundColor,
      borderColor: colors.borderColor,
    };
  };

  const onEventClick = (info) => {
    setCitaSeleccionada({ ...info.event.extendedProps });
    setEditMode(false);
  };
  const confirmarCitaDummy = () => {
    if (!citaSeleccionada) return;
    const id = citaSeleccionada.id;
    const nuevosRaw = rawCitas.map((c) =>
      c.id === id ? { ...c, estado: "confirmada" } : c
    );
    setRawCitas(nuevosRaw);
    setEventos(nuevosRaw.map(mapCitaAEvento));
    setCitaSeleccionada({ ...citaSeleccionada, estado: "confirmada" });
    alert("Cita confirmada (dummy)");
  };
  const cancelarCitaDummy = () => {
    if (!citaSeleccionada) return;
    const id = citaSeleccionada.id;
    const nuevosRaw = rawCitas.map((c) =>
      c.id === id ? { ...c, estado: "cancelada" } : c
    );
    setRawCitas(nuevosRaw);
    setEventos(nuevosRaw.map(mapCitaAEvento));
    setCitaSeleccionada({ ...citaSeleccionada, estado: "cancelada" });
    alert("Cita cancelada (dummy)");
  };
  const guardarEdicionDummy = () => {
    if (!form) return;
    const id = form.id;
    const nuevosRaw = rawCitas.map((c) =>
      c.id === id ? { ...c, ...form } : c
    );
    setRawCitas(nuevosRaw);
    setEventos(nuevosRaw.map(mapCitaAEvento));
    setCitaSeleccionada({ ...form });
    setEditMode(false);
    alert("Cambios guardados (dummy)");
  };
  const contactarCliente = () => {
    if (!citaSeleccionada) return;
    const t = citaSeleccionada.telefono;
    if (t && t.trim().length >= 7) {
      const telefono = t.replace(/\D/g, "");
      window.open(`https://wa.me/57${telefono}`, "_blank");
    } else {
      alert("Teléfono no disponible.");
    }
  };

  const onFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((f) => ({ ...f, [name]: value }));
  };

  const eventosFiltrados = eventos.filter((evt) => {
    const { estado, canal, placa } = evt.extendedProps;
    const cumpleEstado =
      filtros.estado === "todos" || estado === filtros.estado;
    const cumpleCanal = filtros.canal === "todos" || canal === filtros.canal;
    const cumplePlaca =
      filtros.placa.trim() === "" ||
      (placa || "").toLowerCase().includes(filtros.placa.toLowerCase());
    return cumpleEstado && cumpleCanal && cumplePlaca;
  });

  // ModalBody sigue igual
  const ModalBody = () => {
    if (!citaSeleccionada) return null;
    if (!editMode) {
      const displayDateTime = (() => {
        const iso = combinarFechaHoraISO(
          citaSeleccionada.fecha,
          citaSeleccionada.hora
        );
        return iso
          ? new Date(iso).toLocaleString()
          : `${citaSeleccionada.fecha || ""} ${citaSeleccionada.hora || ""}`;
      })();
      return (
        <>
          <div className="modal-body">
            <div className="row">
              <div className="col-8">
                <h5>{citaSeleccionada.servicio || "Servicio"}</h5>
                <p>
                  <strong>Cliente:</strong>{" "}
                  {citaSeleccionada.nombreCliente || "N/A"}
                </p>
                <p>
                  <strong>Placa:</strong> {citaSeleccionada.placa || "N/A"}
                </p>
                <p>
                  <strong>Vehículo:</strong>{" "}
                  {`${citaSeleccionada.tipoVehiculo || ""} ${
                    citaSeleccionada.modeloVehiculo || ""
                  }`.trim() || "N/A"}
                </p>
              </div>
              <div className="col-4 text-right">
                <span
                  className={`badge ${
                    citaSeleccionada.estado === "confirmada"
                      ? "badge-success"
                      : citaSeleccionada.estado === "cancelada"
                      ? "badge-danger"
                      : "badge-primary"
                  } p-2`}
                >
                  {citaSeleccionada.estado}
                </span>
                <p className="mt-2 text-muted small">{displayDateTime}</p>
              </div>
            </div>
            <hr />
            <p>
              <strong>Canal:</strong> {citaSeleccionada.canal || "N/A"}
            </p>
            <p>
              <strong>Fecha registro:</strong>{" "}
              {citaSeleccionada.fechaRegistro
                ? new Date(citaSeleccionada.fechaRegistro).toLocaleString()
                : "N/A"}
            </p>
            <p>
              <strong>Notas:</strong> {citaSeleccionada.notas || "—"}
            </p>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-outline-secondary"
              onClick={() => setEditMode(true)}
            >
              ✏️ Editar
            </button>
            <button className="btn btn-info" onClick={contactarCliente}>
              📞 Contactar
            </button>
            <button className="btn btn-success" onClick={confirmarCitaDummy}>
              ✔ Confirmar
            </button>
            <button className="btn btn-danger" onClick={cancelarCitaDummy}>
              ✖ Cancelar
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setCitaSeleccionada(null)}
            >
              Cerrar
            </button>
          </div>
        </>
      );
    }
    if (!form) return null;
    const onChange = (e) => {
      const { name, value } = e.target;
      setForm({ ...form, [name]: value });
    };
    return (
      <>
        <div className="modal-body">
          <div className="form-group">
            <label>Cliente</label>
            <input
              className="form-control"
              name="nombreCliente"
              value={form.nombreCliente || ""}
              onChange={onChange}
            />
          </div>
          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Fecha</label>
              <input
                className="form-control"
                name="fecha"
                value={(form.fecha || "").split("T")[0]}
                onChange={onChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label>Hora</label>
              <input
                className="form-control"
                name="hora"
                value={form.hora || ""}
                onChange={onChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Servicio</label>
            <input
              className="form-control"
              name="servicio"
              value={form.servicio || ""}
              onChange={onChange}
            />
          </div>
          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Tipo vehículo</label>
              <input
                className="form-control"
                name="tipoVehiculo"
                value={form.tipoVehiculo || ""}
                onChange={onChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label>Modelo</label>
              <input
                className="form-control"
                name="modeloVehiculo"
                value={form.modeloVehiculo || ""}
                onChange={onChange}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={guardarEdicionDummy}>
            💾 Guardar
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => setEditMode(false)}
          >
            Cancelar
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="card card-primary card-outline">
          <div className="card-header">
            <h3 className="card-title">Calendario de Citas</h3>
            <div className="card-tools">
              <button
                className="btn btn-tool"
                title="Refrescar"
                onClick={fetchCitas}
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
          </div>
          <div className="card-body">
            {/* Filtros */}
            <div className="filtros-box mb-3">
              <select
                className="form-control"
                name="estado"
                value={filtros.estado}
                onChange={onFiltroChange}
              >
                <option value="todos">Todos los estados</option>
                <option value="programada">🕒 Programadas</option>
                <option value="confirmada">✔ Confirmadas</option>
                <option value="cancelada">✖ Canceladas</option>
              </select>
              <select
                className="form-control"
                name="canal"
                value={filtros.canal}
                onChange={onFiltroChange}
              >
                <option value="todos">Todos los canales</option>
                <option value="whatsapp">💬 WhatsApp</option>
                <option value="telefono">📞 Teléfono</option>
              </select>
              <input
                className="form-control"
                placeholder="🔍 Buscar por placa..."
                name="placa"
                value={filtros.placa}
                onChange={onFiltroChange}
              />
            </div>

            {loading && (
              <div className="mb-2 text-muted">Cargando citas...</div>
            )}
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              locale={esLocale}
              firstDay={1}
              events={eventosFiltrados}
              eventClick={onEventClick}
              height="auto"
            />
          </div>
        </div>
      </div>

      {citaSeleccionada && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div
                className={`modal-header ${
                  citaSeleccionada.estado === "confirmada"
                    ? "bg-success"
                    : citaSeleccionada.estado === "cancelada"
                    ? "bg-danger"
                    : "bg-primary"
                } text-white`}
              >
                <h5 className="modal-title">Detalle de la Cita</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setCitaSeleccionada(null)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <ModalBody />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarioCitas;
